/**
 * LocationContext — the session-only device-location signal (T39).
 *
 * Separate from the global `AppMode` (planning/shopping) on purpose: `AppMode`
 * is the app-wide operational context the user can switch by hand and which
 * persists as an explicit preference; `LocationContext` is a transient,
 * never-persisted interpretation of WHERE the device is (unknown / home /
 * in-store / elsewhere), carrying the matched selected-store id when in store.
 *
 * The pure geofence (`@shopista/shared/stores` → `detectLocationContext`)
 * computes the value; `useModeDetection` pushes the result here when a fix
 * resolves. Deals reads `matchedStoreId` to default its store filter once. The
 * value lives in React state only — it dies with the session and is never
 * written to storage (AC6).
 *
 * A bare `useLocationContext()` outside the provider returns the `unknown`
 * default, so screens (and component tests) that don't wrap the provider behave
 * as "no location known" rather than throwing.
 */

import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  UNKNOWN_LOCATION_CONTEXT,
  type LocationContext as LocationContextValue,
} from '@shopista/shared/stores';

interface LocationContextStore {
  /** The current session location interpretation. */
  location: LocationContextValue;
  /** Replace the current value (the detection result, session-only). */
  setLocation: (next: LocationContextValue) => void;
}

const Context = createContext<LocationContextStore>({
  location: UNKNOWN_LOCATION_CONTEXT,
  setLocation: () => {
    /* no-op outside the provider */
  },
});

export function LocationContextProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [location, setLocation] = useState<LocationContextValue>(
    UNKNOWN_LOCATION_CONTEXT,
  );
  const value = useMemo<LocationContextStore>(
    () => ({ location, setLocation }),
    [location],
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

/** Read the current session location context. */
export function useLocationContext(): LocationContextValue {
  return useContext(Context).location;
}

/** Get the setter used by `useModeDetection` to push a resolved fix. */
export function useSetLocationContext(): (next: LocationContextValue) => void {
  return useContext(Context).setLocation;
}
